/**
 * One turn, two answers: once the judge has the agent's reply, the AI's bubble gains a
 * switch and shows either side in place, one at a time. The author label switches with it.
 */

import { useSetAtom } from 'jotai';
import { Bot, UserRound } from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';
import { highlightMatches } from '@/modules/review/highlight-matches';
import { Segmented, type SegmentedOption } from '../ui/segmented';
import { setReplySideAtom, useReplySide, type ReplySide } from './reply-side';

/** Icons alone: two words in a chat bubble is two words competing with the reply. */
const options = (authored: boolean): SegmentedOption<ReplySide>[] => [
  { value: 'ai', label: 'AI reply', icon: Bot, iconOnly: true },
  { value: 'agent', label: authored ? 'Your reference reply' : 'Agent reply', icon: UserRound, iconOnly: true },
];

/** The switch sits at the top of the bubble, so a change in reply length falls into the scroll region below it. */
const ReplySideToggle = ({ requestId, authored }: { requestId: string; authored: boolean }) => {
  const side = useReplySide(requestId);
  const setSide = useSetAtom(setReplySideAtom);

  return (
    <Segmented
      className='w-fit'
      options={options(authored)}
      value={side}
      onChange={(next) => setSide(requestId, next)}
    />
  );
};

/** The label line follows the side; `authored` keeps "what was actually sent" off a reply someone typed here. */
export const ComparedReplyLabel = ({
  requestId,
  at,
  authored,
}: {
  requestId: string;
  /** The run's own clock. Omitted where the surface has no per-turn time to show. */
  at?: string;
  authored: boolean;
}) => {
  const side = useReplySide(requestId);
  return side === 'ai' ? (
    <>
      <span className='font-medium text-indigo-600'>AI · Agent</span>
      {at && (
        <>
          <span>·</span>
          <span>{at}</span>
        </>
      )}
    </>
  ) : (
    <>
      <span className='font-medium text-indigo-600'>{authored ? 'Reference reply' : 'Human agent'}</span>
      <span>·</span>
      <span>{authored ? 'written here, never sent' : 'what was actually sent'}</span>
    </>
  );
};

export interface ComparedReplyBodyProps {
  requestId: string;
  aiReply: string;
  agentReply: string[] | null;
  /** How alike the two texts are, 0-1. A fact about the pair, so it sits with the pair. */
  similarity?: number | null;
  /** The compared reply was typed here rather than read off the ticket. */
  authored: boolean;
  /** What the agent said that the AI did not, lit inside the agent's reply. */
  highlights?: string[];
}

/** The bubble's body: the switch, then the side it selects, at its own natural height. */
export const ComparedReplyBody = ({
  requestId,
  aiReply,
  agentReply,
  similarity,
  authored,
  highlights,
}: ComparedReplyBodyProps) => {
  const side = useReplySide(requestId);
  const pending = side === 'agent' && agentReply === null;
  const lit = side === 'agent' && Boolean(highlights?.length);
  const facts = [
    typeof similarity === 'number' ? `${Math.round(similarity * 100)}% similar` : null,
    lit ? 'highlighted: not in the AI reply' : null,
  ].filter(Boolean);

  return (
    <span className='flex flex-col gap-1.5'>
      <span className='flex items-center justify-between gap-2'>
        <span className='text-[10px] leading-4 text-indigo-700/60 tabular-nums'>{facts.join(' · ')}</span>
        <ReplySideToggle requestId={requestId} authored={authored} />
      </span>
      {pending ? (
        <span className='flex flex-col gap-1.5 py-0.5'>
          <Skeleton className='h-2.5 w-full rounded-full bg-indigo-100' />
          <Skeleton className='h-2.5 w-3/4 rounded-full bg-indigo-100' />
        </span>
      ) : (
        <span
          key={side}
          className='block whitespace-pre-wrap wrap-break-word animate-in fade-in-0 duration-200 motion-reduce:animate-none'
        >
          {side === 'ai'
            ? aiReply
            : lit
              ? highlightMatches(agentReply?.join('\n\n') ?? '', highlights ?? [])
              : agentReply?.join('\n\n')}
        </span>
      )}
    </span>
  );
};
